import { 
  collection, 
  query, 
  getDocs, 
  QueryConstraint,
  Timestamp,
  DocumentData,
  FirestoreError,
  QuerySnapshot
} from 'firebase/firestore';
import { db } from './config';
import { handleFirebaseError } from './error-handling';
import { FirebaseError } from 'firebase/app';

// Utility function to handle Firestore queries with retry and error handling
export async function executeQuery<T>(
  collectionName: string,
  constraints: QueryConstraint[],
  mapper: (doc: DocumentData) => T
): Promise<T[]> {
  try {
    const q = query(collection(db, collectionName), ...constraints);
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => mapper({ id: doc.id, ...doc.data() }));
  } catch (error) {
    handleFirebaseError(error as FirestoreError, `Error executing query on ${collectionName}`);
    return []; // Return empty array instead of throwing to handle offline gracefully
  }
}

// Utility function to safely handle snapshots
export function handleSnapshot<T>(
  snapshot: QuerySnapshot<DocumentData>,
  mapper: (doc: DocumentData) => T
): T[] {
  try {
    return snapshot.docs.map(doc => mapper({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error processing snapshot:', error);
    return [];
  }
}

// Utility function to handle Firestore timestamps
export function toDate(timestamp: Timestamp | Date | null | undefined): Date {
  if (!timestamp) return new Date();
  if (timestamp instanceof Date) return timestamp;
  if (timestamp instanceof Timestamp) return timestamp.toDate();
  return new Date(timestamp);
}

// Utility function to create a Firestore timestamp
export function createTimestamp(): Timestamp {
  return Timestamp.now();
}

export const AUTH_ERROR_CODES = {
  EMAIL_IN_USE: 'auth/email-already-in-use',
  INVALID_EMAIL: 'auth/invalid-email',
  WEAK_PASSWORD: 'auth/weak-password',
  NETWORK_ERROR: 'auth/network-request-failed',
  TOO_MANY_REQUESTS: 'auth/too-many-requests',
  OPERATION_NOT_ALLOWED: 'auth/operation-not-allowed',
  INVALID_ARGUMENT: 'auth/invalid-argument',
  USER_NOT_FOUND: 'auth/user-not-found',
  WRONG_PASSWORD: 'auth/wrong-password'
} as const;

export const AUTH_ERROR_MESSAGES = {
  [AUTH_ERROR_CODES.EMAIL_IN_USE]: 'This email is already registered',
  [AUTH_ERROR_CODES.INVALID_EMAIL]: 'Invalid email address',
  [AUTH_ERROR_CODES.WEAK_PASSWORD]: 'Password must be at least 6 characters',
  [AUTH_ERROR_CODES.NETWORK_ERROR]: 'Network error. Please check your connection',
  [AUTH_ERROR_CODES.TOO_MANY_REQUESTS]: 'Too many attempts. Please try again later',
  [AUTH_ERROR_CODES.OPERATION_NOT_ALLOWED]: 'This operation is not allowed',
  [AUTH_ERROR_CODES.INVALID_ARGUMENT]: 'Invalid argument provided',
  [AUTH_ERROR_CODES.USER_NOT_FOUND]: 'User not found',
  [AUTH_ERROR_CODES.WRONG_PASSWORD]: 'Incorrect password',
  default: 'An unexpected error occurred. Please try again.'
} as const;

export function handleFirebaseError(error: unknown, message = 'An error occurred'): never {
  if (error instanceof FirebaseError || error instanceof FirestoreError) {
    const errorCode = error.code as keyof typeof AUTH_ERROR_CODES;
    const errorMessage = AUTH_ERROR_MESSAGES[errorCode] || AUTH_ERROR_MESSAGES.default;
    throw new Error(`${message}: ${errorMessage}`);
  }
  throw error;
}

export function convertFirestoreDates<T extends Record<string, any>>(data: T): T {
  const converted = { ...data };
  for (const [key, value] of Object.entries(converted)) {
    if (value instanceof Date) {
      converted[key] = value;
    }
  }
  return converted;
}